/**
 * 電話帳同期(CS1。GWS 連携のオプション)。
 *
 * 従業員マスタの電話番号を、配布先の人の Google 連絡先へ**ラベル付きで**配る。
 * iPhone では Google アカウントの連絡先として出る(着信時に名前が出る)。
 * 触るのはラベルの中の、こちらが作った連絡先だけ(本人の連絡先には触らない)。
 *
 * 誰に配るか・誰を載せるかは動的グループ + 個別の追加・除外で決める。
 * 個別指定の追加は人事の従業員一覧(チェックして「電話帳: …」)から行う。
 */
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import type { FormInstance } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type {
  ContactBook,
  ContactBookInput,
  ContactBookMember,
  ContactPreviewPerson,
  ContactsSetting,
  ContactSyncRun,
  ContactWithdrawal,
} from "../../lib/api";
import { jst } from "../../lib/format";
import {
  useCancelContactWithdrawal,
  useContactBook,
  useContactGroups,
  useContactPreview,
  useContactPreviewCsv,
  useContactRuns,
  useContactsSetting,
  useContactWithdrawals,
  useSaveContactBook,
  useSaveContactsSetting,
  useSetContactMembers,
  useSyncContacts,
  useTestContacts,
} from "./contactHooks";
import { errorText, useIdpPerms } from "./hooks";

const { Text, Paragraph } = Typography;

const ROLE_LABEL: Record<string, string> = { distribute: "配布先", listing: "掲載" };
const MODE_LABEL: Record<string, string> = { include: "追加", exclude: "除外" };
const RUN_LABEL: Record<string, { text: string; color: string }> = {
  running: { text: "実行中", color: "processing" },
  ok: { text: "成功", color: "success" },
  partial: { text: "一部失敗", color: "warning" },
  failed: { text: "失敗", color: "error" },
};
const TRIGGER_LABEL: Record<string, string> = { manual: "手動", schedule: "定期" };
const DROP_LABEL: Record<string, string> = {
  not_live: "在籍中でない・匿名化済み",
  no_email: "メールが無い",
  no_phone: "電話が無い",
  not_in_gws: "GWS に無い",
  suspended: "GWS で停止中",
  withdrawn: "撤収タスクあり",
};
const BOOK_FIELDS = [
  "name",
  "label_name",
  "interval_min",
  "distribute_group_id",
  "listing_group_id",
  "listing_without_phone",
  "active",
] as const;
const SETTING_FIELDS = ["sa_email", "ops_email"] as const;

/**
 * サーバーの値でフォームを埋める。`setFieldsValue` は「触った」扱いになるので
 * `touched: false` で入れる(MOB2 と同じ。次の読み直しで上書きできるように)。
 */
function fill<T extends object>(
  form: FormInstance<T>,
  data: T | undefined,
  names: readonly string[],
) {
  if (!data) return;
  const src = data as Record<string, unknown>;
  form.setFields(names.map((name) => ({ name: name as never, value: src[name], touched: false })));
}
const STAT_LABEL: [string, string][] = [
  ["users", "配布先"],
  ["created", "作成"],
  ["updated", "更新"],
  ["deleted", "削除"],
  ["withdrawn", "撤収"],
  ["failed", "失敗"],
];

function SettingCard() {
  const { canAdmin } = useIdpPerms();
  const setting = useContactsSetting();
  const save = useSaveContactsSetting();
  const test = useTestContacts();
  const [form] = Form.useForm<ContactsSetting>();
  useEffect(() => {
    // 入力途中に読み直し(フォーカス復帰など)が来ても消さない(fix1 L3)
    if (form.isFieldsTouched()) return;
    fill(form, setting.data, SETTING_FIELDS);
  }, [setting.data, form]);
  return (
    <Card size="small" title="接続(鍵なしのドメイン全体の委任)" style={{ marginBottom: 16 }}>
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        サービスアカウントの鍵は持たない。運用アカウントの OAuth(GWS コネクタの refresh
        token)で IAM の signJwt を呼び、各利用者として連絡先を書く。準備は
        docs/idp-contacts-sync.md を参照。
      </Paragraph>
      <Form
        form={form}
        layout="inline"
        disabled={!canAdmin}
        onFinish={(v) =>
          save.mutate(v, {
            onSuccess: () => {
              form.resetFields();
              void setting.refetch().then((r) => fill(form, r.data, SETTING_FIELDS));
            },
          })
        }
      >
        <Form.Item name="sa_email" label="サービスアカウント">
          <Input style={{ width: 340 }} placeholder="contacts-sync@<project>.iam.gserviceaccount.com" />
        </Form.Item>
        <Form.Item name="ops_email" label="運用アカウント">
          <Input style={{ width: 240 }} placeholder="admin@example.co.jp" />
        </Form.Item>
        <Form.Item>
          <Space>
            {canAdmin && (
              <Button type="primary" htmlType="submit" loading={save.isPending}>
                保存
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
      {canAdmin && (
        <Button style={{ marginTop: 8 }} loading={test.isPending} onClick={() => test.mutate()}>
          接続テスト(運用アカウントの連絡先を 1 件読む)
        </Button>
      )}
    </Card>
  );
}

function BookCard({ book }: { book: ContactBook }) {
  const { canAdmin } = useIdpPerms();
  const groups = useContactGroups();
  const save = useSaveContactBook();
  const books = useContactBook();
  const [form] = Form.useForm<ContactBookInput>();
  useEffect(() => {
    // 入力途中に読み直し(同期の履歴のポーリングなど)が来ても消さない(fix1 L3)
    if (form.isFieldsTouched()) return;
    fill(form, book as ContactBookInput, BOOK_FIELDS);
  }, [book, form]);
  const groupOptions = (groups.data ?? []).map((g) => ({
    value: g.id,
    label: `${g.name}(${g.members} 人)${g.active ? "" : " [停止中]"}`,
  }));
  return (
    <Card size="small" title="電話帳" style={{ marginBottom: 16 }}>
      <Form
        form={form}
        layout="vertical"
        disabled={!canAdmin}
        onFinish={(v) =>
          save.mutate(
            { id: book.id, data: { ...v, distribute_group_id: v.distribute_group_id ?? null, listing_group_id: v.listing_group_id ?? null } },
            {
              onSuccess: () => {
                form.resetFields();
                void books
                  .refetch()
                  .then((r) => fill(form, r.data?.[0] as ContactBookInput | undefined, BOOK_FIELDS));
              },
            },
          )
        }
      >
        <Flex gap={16} wrap>
          <Form.Item name="name" label="名前" style={{ width: 240 }}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            name="label_name"
            label="ラベル名(iPhone の連絡先に出るグループ名)"
            style={{ width: 300 }}
            extra="配布済みの間は変えられない(古いラベルが残るため)"
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            name="interval_min"
            label="定期同期の間隔(分)"
            style={{ width: 200 }}
            extra="0 = 手動だけ。15〜1440"
          >
            <InputNumber min={0} max={1440} style={{ width: "100%" }} />
          </Form.Item>
        </Flex>
        <Flex gap={16} wrap>
          <Form.Item name="distribute_group_id" label="配布先(動的グループ)" style={{ width: 340 }}>
            <Select allowClear options={groupOptions} loading={groups.isLoading}
              placeholder="この人たちの連絡先に配る" />
          </Form.Item>
          <Form.Item name="listing_group_id" label="掲載(動的グループ)" style={{ width: 340 }}>
            <Select allowClear options={groupOptions} loading={groups.isLoading}
              placeholder="この人たちを電話帳に載せる" />
          </Form.Item>
        </Flex>
        <Flex gap={24} wrap align="center">
          <Form.Item name="listing_without_phone" label="電話が無い人も載せる" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="active" label="定期同期を有効にする" valuePropName="checked">
            <Switch />
          </Form.Item>
          {canAdmin && (
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              保存
            </Button>
          )}
        </Flex>
      </Form>
      <Text type="secondary">
        最終実行: {book.last_run_at ? jst(book.last_run_at) : "まだ無い"}
      </Text>
    </Card>
  );
}

function MembersCard({ book }: { book: ContactBook }) {
  const { canRun } = useIdpPerms();
  const set = useSetContactMembers();
  const columns: ColumnsType<ContactBookMember> = [
    { title: "社員番号", dataIndex: "emp_code", width: 110 },
    { title: "氏名", dataIndex: "name", width: 180 },
    { title: "メール", width: 240, dataIndex: "email", },
    { title: "役割", dataIndex: "role", width: 160, render: (v: string) => ROLE_LABEL[v] ?? v },
    {
      title: "指定",
      dataIndex: "mode",
      width: 110,
      render: (v: string) => (
        <Tag color={v === "exclude" ? "red" : "blue"}>{MODE_LABEL[v] ?? v}</Tag>
      ),
    },
    ...(canRun
      ? [
          {
            title: "",
            key: "op",
            width: 110,
            render: (_: unknown, row: ContactBookMember) => (
              <Button
                size="small"
                onClick={() =>
                  set.mutate({
                    id: book.id,
                    employee_ids: [row.employee_id],
                    roles: [row.role],
                    mode: null,
                  })
                }
              >
                指定を外す
              </Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <Card size="small" title="個別の追加・除外" style={{ marginBottom: 16 }}>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        除外は追加・動的グループより強い。追加・除外は人事の従業員一覧で行をチェックして
        「電話帳: …」から行う。
      </Paragraph>
      <Table<ContactBookMember>
        scroll={{ x: "max-content" }}
        rowKey={(r) => `${r.employee_id}-${r.role}`}
        size="small"
        columns={columns}
        dataSource={book.members}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
      />
    </Card>
  );
}

const personColumns = (phones: boolean): ColumnsType<ContactPreviewPerson> => [
  { title: "社員番号", dataIndex: "emp_code", width: 110 },
  { title: "氏名", dataIndex: "name", width: 160 },
  ...(phones
    ? [
        { title: "部署", width: 160, dataIndex: "dept", },
        { title: "役職", dataIndex: "title", width: 160 },
        { title: "会社携帯", dataIndex: "work_mobile", width: 160 },
        { title: "会社電話", dataIndex: "work_phone", width: 160 },
        { title: "内線", dataIndex: "extension", width: 110 },
      ]
    : [
        { title: "メール", width: 240, dataIndex: "email", },
        { title: "部署", width: 160, dataIndex: "dept", },
      ]),
];

function PreviewCard({ book }: { book: ContactBook }) {
  const [shown, setShown] = useState(false);
  const preview = useContactPreview(book.id, shown);
  const csv = useContactPreviewCsv(book.id);
  const p = preview.data;
  return (
    <Card
      size="small"
      title="プレビュー"
      style={{ marginBottom: 16 }}
      extra={
        <Space>
          <Button onClick={() => (shown ? void preview.refetch() : setShown(true))}
            loading={preview.isFetching}>
            {shown ? "再計算" : "プレビュー"}
          </Button>
          <Button onClick={csv}>CSV(掲載)</Button>
        </Space>
      }
    >
      {preview.isError && (
        <Alert type="error" showIcon message="プレビューできなかった"
          description={errorText(preview.error)} />
      )}
      {!p && !preview.isError && (
        <Text type="secondary">今の設定で誰に配り誰を載せるかを計算する(Google には書かない)。</Text>
      )}
      {p && (
        <>
          {!p.gws_checked && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="GWS のアカウント一覧が取れなかった"
              description="GWS に居るか・停止中かを確かめずに数えている。この状態では同期は止まる(GWS コネクタの設定を確認)。"
            />
          )}
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="配布先">
              <span data-testid="contacts-distribute-count">{p.distribute}</span> 人
            </Descriptions.Item>
            <Descriptions.Item label="掲載">
              <span data-testid="contacts-listing-count">{p.listing}</span> 人
            </Descriptions.Item>
            {(["distribute", "listing"] as const).map((role) => (
              <Descriptions.Item key={role} label={`${ROLE_LABEL[role]}から外れた人`}>
                {Object.entries(p.drops[role] ?? {})
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => `${DROP_LABEL[k] ?? k}: ${n}`)
                  .join(" / ") || "なし"}
              </Descriptions.Item>
            ))}
          </Descriptions>
          {p.truncated && (
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="一覧は先頭だけ出している(全員は CSV で確認する)" />
          )}
          <Typography.Title level={5}>配布先</Typography.Title>
          <Table<ContactPreviewPerson> scroll={{ x: "max-content" }} rowKey="id" size="small" columns={personColumns(false)}
            dataSource={p.distribute_list} pagination={{ pageSize: 10, hideOnSinglePage: true }} />
          <Typography.Title level={5}>掲載</Typography.Title>
          <Table<ContactPreviewPerson> scroll={{ x: "max-content" }} rowKey="id" size="small" columns={personColumns(true)}
            dataSource={p.listing_list} pagination={{ pageSize: 10, hideOnSinglePage: true }} />
        </>
      )}
    </Card>
  );
}

function RunsCard({ book }: { book: ContactBook }) {
  const { canRun } = useIdpPerms();
  const runs = useContactRuns(book.id);
  const sync = useSyncContacts();
  const busy = (runs.data ?? []).some((r) => r.status === "running");
  const columns: ColumnsType<ContactSyncRun> = [
    { title: "開始", dataIndex: "started_at", width: 180, render: (v: string) => jst(v) },
    { title: "終了", dataIndex: "finished_at", width: 180, render: (v: string | null) => jst(v) },
    {
      title: "きっかけ",
      dataIndex: "trigger",
      width: 320,
      render: (v: string, r) => `${TRIGGER_LABEL[v] ?? v}${r.actor && v === "manual" ? `(${r.actor})` : ""}`,
    },
    {
      title: "結果",
      dataIndex: "status",
      width: 110,
      render: (v: string) => <Tag color={RUN_LABEL[v]?.color}>{RUN_LABEL[v]?.text ?? v}</Tag>,
    },
    {
      title: "件数",
      width: 480,
      dataIndex: "stats",
      render: (s: Record<string, number>) =>
        STAT_LABEL.filter(([k]) => s?.[k] !== undefined)
          .map(([k, l]) => `${l} ${s[k]}`)
          .join(" / "),
    },
    {
      title: "エラー",
      width: 320,
      onCell: (row) => ({ title: row.error }),
      dataIndex: "error",
      ellipsis: true,
      render: (v: string) => (v ? <Text type="danger" title={v}>{v.split("\n")[0]}</Text> : ""),
    },
  ];
  return (
    <Card
      size="small"
      title="同期の履歴"
      extra={
        canRun && (
          <Popconfirm
            title="今すぐ同期する"
            description="配布先の全員の連絡先(ラベルの中)を書き換える。定期同期が無効でも実行する。"
            onConfirm={() => sync.mutate(book.id)}
            okText="同期する"
            cancelText="やめる"
          >
            <Button type="primary" loading={sync.isPending || busy}>
              手動で同期
            </Button>
          </Popconfirm>
        )
      }
    >
      <Table<ContactSyncRun>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={runs.data ?? []}
        loading={runs.isLoading}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />
    </Card>
  );
}

function WithdrawalsCard() {
  const rows = useContactWithdrawals(true);
  const cancel = useCancelContactWithdrawal();
  const columns: ColumnsType<ContactWithdrawal> = [
    { title: "社員番号", dataIndex: "emp_code", width: 110 },
    { title: "氏名", dataIndex: "name", width: 180 },
    {
      title: "状態",
      dataIndex: "state_label",
      width: 160,
      render: (v: string, r) => (
        <Tag color={r.state === "cancelled" ? "default" : "orange"}>{v}</Tag>
      ),
    },
    { title: "撤収タスク", dataIndex: "task_id", width: 110, render: (v: number | null) => (v ? `#${v}` : "") },
    { title: "更新", dataIndex: "updated_at", width: 180, render: (v: string) => jst(v) },
    { title: "操作者", dataIndex: "actor", width: 160 },
    {
      title: "",
      key: "op",
      width: 130,
      render: (_: unknown, r) =>
        r.state === "cancelled" ? null : (
          <Popconfirm
            title="撤収の記録を取り消す"
            description="次の同期から、配布先の条件に合えばまた配る(復職など)"
            onConfirm={() => cancel.mutate(r.employee_id)}
          >
            <Button size="small">取り消す</Button>
          </Popconfirm>
        ),
    },
  ];
  return (
    <Card size="small" title="撤収の記録" style={{ marginBottom: 16 }}>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        退職・休職の「電話帳撤収」タスクを受けた人。取り消すまで配らない(撤収済み・スキップでも)。
      </Paragraph>
      <Table<ContactWithdrawal>
        scroll={{ x: "max-content" }}
        rowKey="employee_id"
        size="small"
        loading={rows.isLoading}
        columns={columns}
        dataSource={rows.data ?? []}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
      />
    </Card>
  );
}

export function ContactsPage() {
  const { canRun } = useIdpPerms();
  const { book, isError, error, isLoading } = useContactBook();
  return (
    <div>
      <Typography.Title level={3}>電話帳</Typography.Title>
      <SettingCard />
      {isError && (
        <Alert type="error" showIcon message="電話帳を取得できなかった"
          description={errorText(error)} style={{ marginBottom: 16 }} />
      )}
      {isLoading && <Text type="secondary">読み込み中…</Text>}
      {book && (
        <>
          <BookCard book={book} />
          {canRun && <MembersCard book={book} />}
          {canRun && <PreviewCard book={book} />}
          {canRun && <WithdrawalsCard />}
          <RunsCard book={book} />
        </>
      )}
    </div>
  );
}
